package diana.soleil.design.iraniancanadienbazzar.core.data

import diana.soleil.design.iraniancanadienbazzar.core.model.LocalizedText
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow

/** Resolved blocking-gate state for the app shell. At most one gate shows; update wins. */
data class AppGatesState(
    /** Backend/maintenance kill-switch: block the whole app behind a maintenance screen. */
    val maintenance: Boolean = false,
    val maintenanceMessage: LocalizedText = LocalizedText(),
    /** This binary is below `min_supported_app_version`: block behind a force-update screen. */
    val forceUpdate: Boolean = false,
    /** Where the update button sends the user (store listing; defaults to the web app). */
    val updateUrl: String = "",
    /** True when the active gate comes from a Developer Menu override (shows a debug dismiss). */
    val debugForced: Boolean = false,
)

/**
 * App-wide blocking gates, pushed from Remote Config at the composition root (`maintenance_mode_enabled`
 * + `maintenance_message_{en,fr,fa}`, and `min_supported_app_version` + `force_update_url` compared
 * against the running build by the caller). Debug overrides let the Developer Menu preview either
 * screen without touching Remote Config; `null` defers to the fetched values.
 *
 * Both gates render ABOVE navigation (App.kt) — force-update outranks maintenance because it stays
 * true for this binary even after maintenance ends.
 */
class AppGatesConfig {
    private val remote = MutableStateFlow(AppGatesState())
    private val maintenanceOverride = MutableStateFlow<Boolean?>(null)
    private val forceUpdateOverride = MutableStateFlow<Boolean?>(null)

    private val _state = MutableStateFlow(AppGatesState())
    val state: StateFlow<AppGatesState> = _state.asStateFlow()

    /**
     * Push the fetched Remote Config values. [forceUpdate] is pre-computed by the caller (semver
     * compare against the running version) so this class stays platform/build agnostic.
     */
    fun update(
        maintenance: Boolean,
        maintenanceMessage: LocalizedText,
        forceUpdate: Boolean,
        updateUrl: String,
    ) {
        remote.value = AppGatesState(
            maintenance = maintenance,
            maintenanceMessage = maintenanceMessage,
            forceUpdate = forceUpdate,
            updateUrl = updateUrl,
        )
        recompute()
    }

    /** Developer Menu: force/clear the maintenance screen. `null` = follow Remote Config. */
    fun setMaintenanceOverride(enabled: Boolean?) {
        maintenanceOverride.value = enabled
        recompute()
    }

    /** Developer Menu: force/clear the force-update screen. `null` = follow Remote Config. */
    fun setForceUpdateOverride(enabled: Boolean?) {
        forceUpdateOverride.value = enabled
        recompute()
    }

    fun maintenanceOverride(): Boolean? = maintenanceOverride.value
    fun forceUpdateOverride(): Boolean? = forceUpdateOverride.value

    private fun recompute() {
        val maintenance = maintenanceOverride.value ?: remote.value.maintenance
        val forceUpdate = forceUpdateOverride.value ?: remote.value.forceUpdate
        _state.value = remote.value.copy(
            maintenance = maintenance,
            forceUpdate = forceUpdate,
            debugForced = (forceUpdate && forceUpdateOverride.value == true) ||
                (!forceUpdate && maintenance && maintenanceOverride.value == true),
        )
    }
}

/**
 * True when [current] is an older release than [minimum]. Versions are dotted numerics
 * ("1.2.3"); build-metadata suffixes ("1.0.0-dev") are ignored. Unparseable input never blocks.
 */
fun isVersionBelow(current: String, minimum: String): Boolean {
    fun parse(v: String): List<Int>? =
        v.substringBefore('-').split('.').map { it.trim().toIntOrNull() ?: return null }
    val cur = parse(current) ?: return false
    val min = parse(minimum) ?: return false
    val n = maxOf(cur.size, min.size)
    for (i in 0 until n) {
        val c = cur.getOrElse(i) { 0 }
        val m = min.getOrElse(i) { 0 }
        if (c != m) return c < m
    }
    return false
}
